import * as React from "react";
import styled from "styled-components";

import tos, { TOS } from "../../data/tos";
import AppScreen from "../components/Screen";
import SectionThumbnail, {
  FakeMarginLeft,
  FakeMarginRight,
  SectionThumbnailImage,
  THUMBNAIL_TRANSITION_DURATION,
  Z_INDEX_ON_TOP
} from "../components/SectionThumbnail";
import Label from "../components/shared/Label";
import DocumentSection from "../components/structure/DocumentSection";
import { InOffAnimationProps, onOffAnimation } from "../styles/animations";
import { colors, viewports } from "../styles/variables";

const sectionStateDefaults = {
  picTranslateY: 0,
  marginTranslate: 0,
  visible: false,
  inFront: false,
  ref: null as any
};

interface State {
  annotatedDocument: TOS;
  /** Denormalized data */
  sectionsStyles: Array<typeof sectionStateDefaults>;
  sectionsValidation: boolean[];
}

export default class ATCPLayer extends React.Component<{}, State> {
  state = {
    annotatedDocument: tos,
    // Dynamically set defaults when sections change
    sectionsStyles: tos.sections.map(_ => ({ ...sectionStateDefaults })),
    sectionsValidation: tos.sections.map(_ => false)
  };

  toggleDetails = (index: number) => {
    return () => {
      if (!this.state.sectionsStyles[index].visible) {
        if (this.state.sectionsStyles[index].ref) {
          const node: any = this.state.sectionsStyles[index].ref;
          const rect = node.getBoundingClientRect();

          this.setState(({ sectionsStyles }) => ({
            sectionsStyles: [
              ...sectionsStyles.slice(0, index),
              {
                ...sectionsStyles[index],
                visible: true,
                picTranslateY: -rect.top,
                marginTranslate: 20,
                inFront: true
              },
              ...sectionsStyles.slice(index + 1)
            ]
          }));
        }
      } else {
        const node: any = this.state.sectionsStyles[index].ref;
        node.scrollTop = 0;
        this.setState(({ sectionsStyles }) => ({
          sectionsStyles: [
            ...sectionsStyles.slice(0, index),
            { ...sectionsStyles[index], ...sectionStateDefaults },
            ...sectionsStyles.slice(index + 1)
          ]
        }));
      }
    };
  };

  handleSectionValidation = (index: number) => {
    return () => {
      this.setState(({ sectionsValidation }) => ({
        sectionsValidation: [...sectionsValidation.slice(0, index), true, ...sectionsValidation.slice(index + 1)]
      }));
    };
  };

  finalValidation = () => {
    alert("Pending integration");
  };

  render() {
    return (
      <React.Fragment>
        {this.state.annotatedDocument.sections.map((section, i) => (
          <AppScreen
            visible={this.state.sectionsStyles[i].visible}
            inFront={this.state.sectionsStyles[i].inFront}
            scrollRewindOnUpdate
            key={i}
          >
            <DocumentSection
              title={section.title}
              description={section.description}
              averageReadTime={section.averageReadTime}
              itemGroups={section.itemsGroups}
              background={section.background}
              visible={this.state.sectionsStyles[i].visible}
              close={this.toggleDetails(i)}
              validated={this.state.sectionsValidation[i]}
              onSectionValidate={this.handleSectionValidation(i)}
            />
          </AppScreen>
        ))}
        <AppScreen visible inFront={!this.state.sectionsStyles.some(s => s.inFront)} key="list23283">
          <Background
            onTop={!this.state.sectionsStyles.some(sS => sS.inFront)}
            visible={this.state.sectionsStyles.some(sS => sS.inFront)}
          />
          <div>
            <DocumentHeader>
              <DocumentVersion>
                {new Date(this.state.annotatedDocument.metadata.version).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
              </DocumentVersion>
              <DocumentTitle>{this.state.annotatedDocument.metadata.title}</DocumentTitle>
            </DocumentHeader>

            {this.state.annotatedDocument.sections.map((section, i) => (
              <SectionThumbnail
                picTranslateY={this.state.sectionsStyles[i].picTranslateY}
                onClick={this.toggleDetails(i)}
                ref={(ref: any) => (this.state.sectionsStyles[i].ref = ref)}
                onTop={this.state.sectionsStyles[i].visible}
                key={i}
              >
                <SectionThumbnailImage background={section.background} />
                <FakeMarginLeft marginTranslateX={this.state.sectionsStyles[i].marginTranslate} />
                <FakeMarginRight marginTranslateX={this.state.sectionsStyles[i].marginTranslate} />
                <TopLeftCornerRadius marginTranslateX={this.state.sectionsStyles[i].marginTranslate} />
                <BottomLeftCornerRadius marginTranslateX={this.state.sectionsStyles[i].marginTranslate} />
                <TopRightCornerRadius marginTranslateX={this.state.sectionsStyles[i].marginTranslate} />
                <BottomRightCornerRadius marginTranslateX={this.state.sectionsStyles[i].marginTranslate} />
                <SectionThumbnailContainer>
                  <ReadTime visible={!this.state.sectionsStyles[i].visible}>{section.averageReadTime / 60} minutes</ReadTime>
                  <ThumbnailTitle visible={!this.state.sectionsStyles[i].visible}>{section.title}</ThumbnailTitle>
                  <LabelContainer visible={!this.state.sectionsStyles[i].visible}>
                    {this.state.sectionsValidation[i] ? (
                      <Label text="Accepté" accepted />
                    ) : (
                      <Label text="Lire et accepter" accepted={false} />
                    )}
                  </LabelContainer>
                </SectionThumbnailContainer>
              </SectionThumbnail>
            ))}

            <OverviewContainer>
              <OverviewTitle>Les points importants</OverviewTitle>
              {this.state.annotatedDocument.overview.map((overview, index) => (
                <MainPoint key={index}>
                  <MainPointIcon>
                    <i className={"fas " + overview.icon} />
                  </MainPointIcon>
                  <div>
                    <MainPointTitle>{overview.title}</MainPointTitle>
                    <div>{overview.content}</div>
                  </div>
                </MainPoint>
              ))}
            </OverviewContainer>
          </div>
          <SubmitButton active={this.state.sectionsValidation.every(sV => sV)} onClick={this.finalValidation}>
            {this.state.sectionsValidation.every(sV => sV) ? "Passer à la suite" : "Sections en attente de validation"}
          </SubmitButton>
        </AppScreen>
      </React.Fragment>
    );
  }
}

interface SubmitButtonProps {
  active: boolean;
}

const SubmitButton = styled.div<SubmitButtonProps>`
  bottom: 0;
  text-align: center;
  color: white;
  margin: 20px;
  padding: 1rem 10px;
  font-weight: 600;
  background: ${({ active }) => (active ? colors.brand : "#bdbdbd")};
  border-radius: 5px;

  ${viewports.desktop`
    max-width: 600px;
    margin: 20px auto;
  `};
`;

const ThumbnailTitle = styled.div<InOffAnimationProps>`
  flex: 1;
  font-size: 1.6rem;
  line-height: 120%;
  font-weight: 700;
  padding: 0 40px;
  will-change: transform;
  ${onOffAnimation({ delay: 20 }, {})};
`;

const SectionThumbnailContainer = styled.div`
  display: flex;
  position: relative;
  height: 100%;
  flex-direction: column;
`;

const ReadTime = styled.div<InOffAnimationProps>`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 20px;
  margin-left: 40px;
  text-transform: uppercase;
  letter-spacing: 1px;
  will-change: transform, opacity;
  ${onOffAnimation({ delay: 150 }, { delay: 100 })};
`;

const DocumentHeader = styled.div`
  margin: 20px;
  ${viewports.desktop`
    max-width: 600px;
    margin: 20px auto;
  `};
`;

const DocumentTitle = styled.div`
  font-size: 2rem;
  line-height: 120%;
  font-weight: 700;
  padding: 0.2rem 0;
`;

const DocumentVersion = styled.div`
  text-transform: uppercase;
  opacity: 0.6;
`;

const LabelContainer = styled.div<InOffAnimationProps>`
  margin: 20px 40px;
  will-change: opacity, transform;
  ${onOffAnimation({ delay: 100 }, { delay: 50 })};
`;

const OverviewContainer = styled.div`
  margin: 20px;
  ${viewports.desktop`
    max-width: 600px;
    margin: 20px auto;
  `};
`;

const MainPoint = styled.div`
  display: flex;
  margin: 10px 0;
  padding: 20px;
  border: solid 1px #ececec;
  border-radius: 5px;
`;

const MainPointTitle = styled.div`
  font-weight: 600;
`;

const MainPointIcon = styled.div`
  padding-right: 10px;
  color: ${colors.brand};
`;

const OverviewTitle = styled.div`
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.2px;
`;

interface CornerRadiusProps {
  marginTranslateX: number;
}

const TopLeftCornerRadius = styled.div<CornerRadiusProps>`
  position: absolute;
  top: 0;
  left: 20px;
  width: 3px;
  height: 3px;
  background-repeat: no-repeat;
  background-image: url(./corner-top-left.svg);
  transform: translateX(${props => -props.marginTranslateX}px) translateZ(0);
  transition: transform ${THUMBNAIL_TRANSITION_DURATION}ms cubic-bezier(0.17, 0.67, 0.23, 0.89);
`;

const BottomLeftCornerRadius = styled.div<CornerRadiusProps>`
  position: absolute;
  bottom: 0;
  left: 20px;
  width: 3px;
  height: 3px;
  background-repeat: no-repeat;
  background-image: url(./corner-top-left.svg);
  transform: rotate(-90deg) translateY(${props => -props.marginTranslateX}px) translateZ(0);
  transition: transform ${THUMBNAIL_TRANSITION_DURATION}ms cubic-bezier(0.17, 0.67, 0.23, 0.89);
`;

const TopRightCornerRadius = styled.div<CornerRadiusProps>`
  position: absolute;
  top: 0;
  right: 20px;
  width: 3px;
  height: 3px;
  background-repeat: no-repeat;
  background-image: url(./corner-top-left.svg);
  transform: rotate(90deg) translateY(${props => -props.marginTranslateX}px) translateZ(0);
  transition: transform ${THUMBNAIL_TRANSITION_DURATION}ms cubic-bezier(0.17, 0.67, 0.23, 0.89);
`;

const BottomRightCornerRadius = styled.div<CornerRadiusProps>`
  position: absolute;
  bottom: 0;
  right: 20px;
  width: 3px;
  height: 3px;
  background-repeat: no-repeat;
  background-image: url(./corner-top-left.svg);
  transform: rotate(180deg) translateX(${props => -props.marginTranslateX}px) translateZ(0);
  transition: transform ${THUMBNAIL_TRANSITION_DURATION}ms cubic-bezier(0.17, 0.67, 0.23, 0.89);
`;

interface BackgroundProps {
  onTop: boolean;
}

const Background = styled.div<BackgroundProps & InOffAnimationProps>`
  position: fixed;
  z-index: ${({ onTop }) => (onTop ? "0" : Z_INDEX_ON_TOP - 100)};
  display: ${({ onTop }) => (onTop ? "none" : "block")};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  ${onOffAnimation({ duration: 200, delay: 500 }, { duration: 50, delay: 0 })};
`;



// WEBPACK FOOTER //
// ./src/pages/index.tsx